import { useClassroom } from '@/hooks/useClassroom/useClassroom';
import { useClassroomSession } from '@/hooks/useClassroomSession/useClassroomSession';
import { useSession } from '@/providers/session/SessionProvider';
import { Classroom } from '@/types/classroom';
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { useNotifications } from '@toolpad/core';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useBroadcast } from 'use-broadcast-ts';
import { capture } from '../error';

export const ResumeClassroomHandler = () => {
    // provider
    const { t } = useTranslation();
    const { session } = useSession();
    const notifications = useNotifications();
    const navigate = useNavigate();
    const { state } = useBroadcast<{ type: string }>('classroom_channel');

    // ui state
    const [terminating, setTerminating] = React.useState(false);
    const [open, setOpen] = React.useState(false);
    const [ongoingClassroom, setOngoingClassroom] = React.useState<Classroom | undefined>(undefined);

    const { fetch, terminate: terminateClassroom } = useClassroom();
    const { terminate: terminateClassroomSession } = useClassroomSession(ongoingClassroom);

    // Init ongoing classroom
    useEffect(() => {
        if (session) {
            fetch('ongoing').then((classrooms) => {
                if (classrooms && classrooms.length > 0) {
                    console.log(`[ResumeClassroomHandler] init ongoing classrooms${classrooms[0]?.id}`);
                    setOngoingClassroom(classrooms[0]);
                }
            });
        }
    }, [fetch, session]);

    // Reset ongoing classroom start/terminate
    useEffect(() => {
        if (state) {
            switch (state.type) {
                case 'terminated':
                    setOngoingClassroom(undefined);
                    break;
                case 'started':
                    fetch('ongoing').then((classrooms) => {
                        if (classrooms && classrooms.length > 0) {
                            console.log(`[ResumeClassroomHandler] new ongoing classroom ${classrooms[0]?.id}`);
                            setOngoingClassroom(classrooms[0]);
                        }
                    });
                    break;
            }
        }
    }, [fetch, state]);

    // Whenever the location changes, check if the user is in a classroom
    useEffect(() => {
        const pathname = window.location.pathname;
        if (session && !pathname.startsWith('/classrooms/') && ongoingClassroom) {
            setOpen(true);
        }
    }, [ongoingClassroom, session]);

    return (
        <Dialog open={open}>
            <DialogTitle>{t('classroom.resume.title')}</DialogTitle>
            <DialogContent>
                <Typography>{t('classroom.resume.message', { classroomName: ongoingClassroom?.testroomName ?? '' })}</Typography>
            </DialogContent>
            <DialogActions>
                <Button
                    loading={terminating}
                    onClick={async () => {
                        if (ongoingClassroom) {
                            setTerminating(true);
                            try {
                                await terminateClassroomSession();
                                await terminateClassroom(ongoingClassroom);
                            } catch (error) {
                                notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
                                capture(error);
                            } finally {
                                setTerminating(false);
                                setOpen(false);
                            }
                        } else {
                            setOpen(false);
                        }
                    }}
                    loadingIndicator={<CircularProgress size={16} />}
                >
                    {t('classroom.resume.actions.terminate')}
                </Button>
                <Button
                    variant='contained'
                    onClick={() => {
                        if (ongoingClassroom) {
                            navigate(`/classrooms/${ongoingClassroom.id}`, { state: { classroomId: ongoingClassroom.id }, viewTransition: true });
                        }
                        setOpen(false);
                    }}
                >
                    {t('classroom.resume.actions.resume')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
